import React from 'react';
import PropTypes from 'prop-types';
import AnimatedProductCard from '../../../components/common/AnimatedProductCard';
import { findVariantByAttributeValues } from '../../../utils/variants';
import drupalPubSubService from '../../../services/drupalPubSub';
import ExtendWarrantyPlaceholder from '../../../components/ExtendWarrantyPlaceholder';
import styles from './ProductPartSelection.module.scss';
import useDrupalSettings from '../../../hooks/useDrupalSettings';

const ProductPartSelection = ({
  bedframeSelector,
  selectedProductVariant = undefined,
  onChangeSelectedProductPart,
  detectedWarrantyPlansForPlaceholders,
}) => {
  const { product: productFromDrupalSettings } =
    useDrupalSettings();

  const onClick = () => {
    if (bedframeSelector?.ctaLink?.micromodalTrigger) {
      drupalPubSubService.publish({
        subscription: 'onShowModal',
        parameters: {
          micromodalTrigger: bedframeSelector.ctaLink.micromodalTrigger,
        },
      });
    }
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter') {
      onClick();
    }
  };

  if (bedframeSelector && bedframeSelector.productParts) {
    return (
      <>
        <div className={styles['title-container']}>
          <span className={styles.title}>{bedframeSelector.sectionTitle}</span>
          {bedframeSelector.ctaLink && (
            <span className={styles['title-link']}>
              <div
                onClick={onClick}
                onKeyDown={onKeyDown}
                role="button"
                tabIndex={0}
              >
                {bedframeSelector.ctaLink.linkText}
              </div>
            </span>
          )}
        </div>
        {bedframeSelector.description && (
          <div className={styles.description}>
            {bedframeSelector.description}
          </div>
        )}
        {bedframeSelector.productParts.map((productPart) => {
          const currentVariant =
            (selectedProductVariant?.attributes &&
              findVariantByAttributeValues({
                variants: productPart.variants,
                attributeValues: selectedProductVariant?.attributes,
              })) ||
            productPart.variants[0];

          const isSelected = currentVariant.sku === selectedProductVariant?.sku;
          // TODO: Confirm if we will use "More Information" or "Learn More" approach for extra info

          return (
            <AnimatedProductCard
              key={productPart.key}
              id={productPart.id}
              title={productPart.name}
              description={productPart.description}
              name={productPart.name}
              price={currentVariant.price}
              discount={
                currentVariant.discount?.centAmount
                  ? currentVariant.discount
                  : undefined
              }
              imageURL={currentVariant.image}
              isSelected={isSelected}
              onChange={onChangeSelectedProductPart}
              hasVariants={false}
              displayDiscount={currentVariant.discount?.centAmount > 0}
            />
          );
        })}
        <ExtendWarrantyPlaceholder
          placeholderId="extend-offer-primary-product"
          productType={productFromDrupalSettings?.productType}
          detectedWarrantyPlansForPlaceholders={detectedWarrantyPlansForPlaceholders}
          selectedProduct={selectedProductVariant?.sku}
        />
      </>
    );
  }
  return null;
};

ProductPartSelection.propTypes = {
  bedframeSelector: PropTypes.shape({
    sectionTitle: PropTypes.string,
    description: PropTypes.string,
    ctaLink: PropTypes.shape({
      linkText: PropTypes.string.isRequired,
      micromodalTrigger: PropTypes.string,
    }),
    productParts: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        key: PropTypes.string.isRequired,
        variants: PropTypes.arrayOf(
          PropTypes.shape({
            sku: PropTypes.string.isRequired,
            masterVariant: PropTypes.bool,
            attributes: PropTypes.arrayOf(
              PropTypes.shape({
                name: PropTypes.string.isRequired,
                key: PropTypes.string,
                label: PropTypes.string,
              }),
            ),
          }),
        ),
      }),
    ).isRequired,
  }).isRequired,
  selectedProductVariant: PropTypes.shape({
    sku: PropTypes.string.isRequired,
    attributes: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        key: PropTypes.string,
        label: PropTypes.string,
      }),
    ),
  }),
  onChangeSelectedProductPart: PropTypes.func.isRequired,
  detectedWarrantyPlansForPlaceholders: PropTypes.object.isRequired,
};

export default ProductPartSelection;
